import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mic, MicOff, PhoneOff, Phone, MessageSquare, Volume2, VolumeX, X, Sparkles } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useKalkiVoice, VoiceState } from "@/hooks/useKalkiVoice";
import KalkiGlobalHeader from "@/components/kalki/KalkiGlobalHeader";
import { ConversationMode } from "@/hooks/voiceStateMachine";
import SignInRequired from "@/components/SignInRequired";

/**
 * TalkKrishna — isolated two-way voice conversation page.
 * Route: /app/talk  (diagnostic: /app/talk?debug=1)
 *
 * Design:
 *  - Full-screen graphite black #1C1C1C.
 *  - User DP is the visual center; one luminous ring + waveform ring + aura
 *    around it reacts to real audio amplitude when Krishna is speaking.
 *  - Six-state machine: idle | listening | thinking | preparing_audio | speaking | error.
 *  - Mobile fits within 100dvh. No cards, no dashboard, no Earth.
 */

const INDIGO = "#5D00FF";
const FUCHSIA = "#FF2E9A";
const LIME = "#CFFF04";
const GRAPHITE = "#1C1C1C";

const KRISHNA_MEDALLION = "/assets/krishna/krishna-medallion.png";

function StateMessage({ state }) {
  if (state === VoiceState.IDLE) return <>TAP TO TALK</>;
  if (state === VoiceState.CONNECTING) return <>CONNECTING…</>;
  if (state === VoiceState.READY) return <>READY TO LISTEN</>;
  if (state === VoiceState.LISTENING) return <><span style={{ color: LIME }}>●</span>&nbsp;&nbsp;LISTENING TO YOU</>;
  if (state === VoiceState.USER_SPEAKING) return <><span style={{ color: LIME }}>●</span>&nbsp;&nbsp;LISTENING TO YOU</>;
  if (state === VoiceState.USER_TURN_FINALIZING) return <>✦&nbsp;&nbsp;CAPTURING YOUR WORDS</>;
  if (state === VoiceState.TRANSCRIBING) return <>✦&nbsp;&nbsp;UNDERSTANDING YOUR INTENTION</>;
  if (state === VoiceState.THINKING) return <>✦&nbsp;&nbsp;REFLECTING ON YOUR QUESTION</>;
  if (state === VoiceState.KRISHNA_THINKING) return <>✦&nbsp;&nbsp;REFLECTING ON YOUR QUESTION</>;
  if (state === VoiceState.PREPARING_AUDIO) return <>✦&nbsp;&nbsp;PREPARING MY VOICE…</>;
  if (state === VoiceState.SPEAKING) return <>✦&nbsp;&nbsp;KALKI IS SPEAKING</>;
  if (state === VoiceState.KRISHNA_SPEAKING) return <>✦&nbsp;&nbsp;KALKI IS SPEAKING</>;
  if (state === VoiceState.ERROR) return <>🔊&nbsp;&nbsp;TAP TO ENABLE VOICE</>;
  return null;
}

/** Visual rings around the HERO Krishna medallion. */
function KalkiRings({ state, amplitude }) {
  const speaking = state === VoiceState.SPEAKING;
  const listening = state === VoiceState.LISTENING;
  const thinking = state === VoiceState.THINKING || state === VoiceState.PREPARING_AUDIO;

  const amp = speaking ? amplitude : 0;
  const amp2 = Math.min(1, amp * 1.3);

  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
      {/* OUTER AURA — pulses when Krishna speaks */}
      <div
        className="absolute rounded-full transition-[opacity] duration-500"
        style={{
          width: `calc(100% + ${speaking ? 140 + amp * 220 : listening ? 80 : thinking ? 110 : 60}px)`,
          height: `calc(100% + ${speaking ? 140 + amp * 220 : listening ? 80 : thinking ? 110 : 60}px)`,
          background: speaking
            ? `radial-gradient(circle, ${FUCHSIA}33 0%, ${INDIGO}1a 42%, transparent 72%)`
            : thinking
            ? `radial-gradient(circle, ${INDIGO}22 0%, ${FUCHSIA}11 50%, transparent 72%)`
            : `radial-gradient(circle, ${INDIGO}14 0%, transparent 70%)`,
          filter: "blur(22px)",
          opacity: state === VoiceState.IDLE ? 0.55 : 1,
          transform: speaking ? `scale(${1 + amp * 0.1})` : "scale(1)",
          transition: "transform 80ms linear",
        }}
      />

      {/* MID WAVEFORM RING */}
      <div
        className="absolute rounded-full"
        style={{
          width: `calc(100% + ${64 + amp2 * 90}px)`,
          height: `calc(100% + ${64 + amp2 * 90}px)`,
          border: `1px solid ${speaking ? FUCHSIA : INDIGO}77`,
          boxShadow: speaking
            ? `0 0 ${34 + amp * 50}px ${FUCHSIA}66, inset 0 0 ${24 + amp * 36}px ${INDIGO}44`
            : listening
            ? `0 0 20px ${INDIGO}44`
            : thinking
            ? `0 0 28px ${INDIGO}66`
            : "none",
          opacity: state === VoiceState.IDLE ? 0.35 : 0.9,
          animation: thinking ? "kalki-rotate 6s linear infinite" : (speaking ? "kalki-breathe 1.6s ease-in-out infinite" : "none"),
        }}
      />

      {/* INNER LUMINOUS RING — hugs the medallion */}
      <div
        className="absolute rounded-full"
        style={{
          width: `calc(100% + ${18 + amp * 32}px)`,
          height: `calc(100% + ${18 + amp * 32}px)`,
          border: `2px solid ${speaking ? FUCHSIA : INDIGO}`,
          boxShadow: speaking
            ? `0 0 ${22 + amp * 46}px ${FUCHSIA}, inset 0 0 ${12 + amp * 22}px ${INDIGO}99`
            : `0 0 16px ${INDIGO}aa`,
          opacity: state === VoiceState.IDLE ? 0.6 : 1,
          transition: "all 120ms linear",
        }}
      />
    </div>
  );
}

/** Main HERO — Lord Krishna medallion. Pulses only while SPEAKING. */
function KrishnaHero({ state, amplitude }) {
  const speaking = state === VoiceState.SPEAKING;
  const scale = speaking ? 1 + amplitude * 0.04 : 1.0;
  return (
    <div
      className="relative rounded-full overflow-hidden"
      style={{
        width: "clamp(220px, 32vmin, 340px)",
        height: "clamp(220px, 32vmin, 340px)",
        transform: `scale(${scale})`,
        transition: "transform 90ms linear",
        boxShadow: speaking
          ? `0 20px 70px rgba(255,46,154,0.35), inset 0 0 0 2px rgba(255,255,255,0.1)`
          : `0 20px 60px rgba(93,0,255,0.3), inset 0 0 0 2px rgba(255,255,255,0.08)`,
      }}
      data-testid="talk-krishna-hero"
    >
      <img
        src={KRISHNA_MEDALLION}
        alt="Lord Krishna"
        draggable={false}
        className="w-full h-full object-cover"
        style={{
          animation: speaking ? "krishna-breath 2.4s ease-in-out infinite" : "none",
        }}
      />
    </div>
  );
}

/** Small satellite — the user's DP. Animates only while LISTENING. */
function UserBadge({ user, state }) {
  const listening = state === VoiceState.LISTENING;
  return (
    <div
      className="absolute"
      data-testid="talk-user-badge"
      style={{
        bottom: "-6px",
        right: "6px",
        zIndex: 2,
      }}
    >
      {/* speaking-into-mic halo while LISTENING */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          boxShadow: listening ? `0 0 0 3px ${LIME}99, 0 0 24px ${LIME}66` : "none",
          animation: listening ? "user-pulse 1.1s ease-in-out infinite" : "none",
          transition: "box-shadow 220ms ease",
        }}
      />
      <div
        className="relative rounded-full overflow-hidden"
        style={{
          width: "clamp(56px, 7vmin, 76px)",
          height: "clamp(56px, 7vmin, 76px)",
          border: "3px solid #1C1C1C",
          background: GRAPHITE,
        }}
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user?.name || "You"}
            referrerPolicy="no-referrer"
            draggable={false}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-white text-2xl font-light"
            style={{ background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})` }}
          >
            {(user?.name || "Y").slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
      {/* tiny mic dot while listening */}
      {listening && (
        <span
          className="absolute -bottom-1 -right-1 rounded-full"
          style={{
            width: 14, height: 14,
            background: LIME,
            boxShadow: `0 0 10px ${LIME}cc`,
            border: `2px solid ${GRAPHITE}`,
          }}
        />
      )}
    </div>
  );
}

export default function TalkKrishna() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const debug = search.get("debug") === "1";
  const { user, loading } = useAuth();

  // Audio toggle — when OFF, Krishna responds in text only (no TTS/play).
  const [audioOn, setAudioOn] = useState(true);
  // Direct-test busy flag (bypasses STT + LLM — straight ElevenLabs probe).
  const [testing, setTesting] = useState(false);
  // Live diagnostic panel data populated by the TEST button + hook events.
  const [diag, setDiag] = useState({ status: null, type: null, bytes: null, playing: false, curr: 0, dur: 0, playPromise: null, error: null });

  const conversationId = useMemo(
    () => (user ? `kalki_${user.user_id}` : "kalki_guest"),
    [user]
  );
  const voice = useKalkiVoice({ conversationId, audioOn });
  const voiceState = voice.voiceState;

  // ========== TAP-TO-TALK CONTROLLER (Voice Talk 3.0, 2026-02) ==========
  // Must be declared BEFORE any early return — React hooks rules.
  //
  // Design (per P0 spec):
  //    IDLE → tap → LISTENING → tap-again → UNDERSTANDING → ... → SPEAKING → tap → LISTENING
  //
  // Behavior:
  //   • Tap while IDLE/READY/ERROR → startTalking() (first turn) or beginNextTurn().
  //   • Tap while LISTENING/USER_SPEAKING → finalizeTurn() (commit + send).
  //   • Tap while SPEAKING or PREPARING_AUDIO → barge() (hard-stop Krishna's
  //     audio + cancel its session) → immediately start next recording. This
  //     is the "zero overlap" requirement: no two audio streams, no stale
  //     response bleeds in.
  //   • Rapid double-tap guard — ignores a second tap within 350 ms of the
  //     previous one so a trembling finger doesn't produce a half-captured
  //     turn.
  const lastTapRef = useRef(0);
  const TAP_DEBOUNCE_MS = 350;

  const onTapTalk = React.useCallback(async () => {
    const now = Date.now();
    if (now - lastTapRef.current < TAP_DEBOUNCE_MS) return;
    lastTapRef.current = now;

    const s = voiceState;
    try {
      if (s === VoiceState.IDLE || s === VoiceState.ERROR) {
        await voice.startTalking();
        return;
      }
      if (s === VoiceState.READY || s === VoiceState.ENDING) {
        await voice.beginNextTurn();
        return;
      }
      if (s === VoiceState.LISTENING || s === VoiceState.USER_SPEAKING) {
        voice.finalizeTurn();
        return;
      }
      if (s === VoiceState.SPEAKING || s === VoiceState.PREPARING_AUDIO ||
          s === VoiceState.KRISHNA_SPEAKING || s === VoiceState.KRISHNA_THINKING) {
        // Zero-overlap barge: synchronously stop Krishna's audio + abort
        // any in-flight TTS fetch, then begin the next recording. `barge`
        // now transitions to READY synchronously, so `beginNextTurn` sees
        // the right state without needing a setTimeout.
        try { voice.barge(); } catch (_) {}
        try { await voice.beginNextTurn(); } catch (_) {}
        return;
      }
      // THINKING / KRISHNA_THINKING / CONNECTING etc. → ignore
    } catch (_e) {
      // noop — hook sets its own ERROR state
    }
  }, [voice, voiceState]);

  // Safety: if the page is hidden mid-recording (phone locked, app backgrounded),
  // auto-commit so the mic never stays open and no stale listening lingers.
  useEffect(() => {
    const vis = () => {
      if (!document.hidden) return;
      const s = voiceState;
      if (s === VoiceState.LISTENING || s === VoiceState.USER_SPEAKING) {
        try { voice.finalizeTurn(); } catch (_) {}
      }
      if (s === VoiceState.SPEAKING || s === VoiceState.PREPARING_AUDIO) {
        try { voice.barge(); } catch (_) {}
      }
    };
    document.addEventListener("visibilitychange", vis);
    return () => document.removeEventListener("visibilitychange", vis);
  }, [voice, voiceState]);

  // Tiny holding-flag used by the visual feedback (pulsing avatar).
  // We derive it from state so there is ONE source of truth (no PTT remnant).
  const listening = voiceState === VoiceState.LISTENING ||
                    voiceState === VoiceState.USER_SPEAKING;

  // Voice 6.0 — proactive mic-permission probe. On page mount we check the
  // Permissions API (where supported); if NOT granted we surface a one-time
  // "Enable Microphone" button that triggers the real browser prompt via a
  // user gesture (required by iOS Safari & Chrome autoplay policies). If
  // already granted, this component renders nothing and the push-to-talk
  // mic works as expected. MUST be declared before any conditional return.
  const [micPerm, setMicPerm] = useState("unknown");
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const s = await navigator.permissions.query({name: "microphone"});
          if (!dead) setMicPerm(s.state);
          s.onchange = () => { if (!dead) setMicPerm(s.state); };
          return;
        }
      } catch (_) {}
      if (!dead) setMicPerm("prompt");
    })();
    return () => { dead = true; };
  }, []);
  const requestMic = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      stream.getTracks().forEach((t) => t.stop());
      setMicPerm("granted");
    } catch (_) {
      setMicPerm("denied");
    }
  }, []);

  // Mobile fix (Oct 2026) — instead of silently bouncing to /app (which felt
  // like Talk was "not opening" on iPhone), render a visible sign-in screen
  // when there is no auth cookie. Must come AFTER all hooks have mounted.
  if (!loading && !user) {
    return (
      <SignInRequired
        variant="dark"
        title="Sign in to Talk with Krishna"
        subtitle="Your voice session is private — a quick sign-in unlocks the call and keeps your conversation safe."
      />
    );
  }

  if (debug) return <VoiceDiagnostic voice={voice} user={user} />;

  const { transcript, kalkiText, experience, vedicSky, amplitude, errorMsg, supportsSpeech } = voice;
  const idle = voiceState === VoiceState.IDLE;
  const liveText =
    voiceState === VoiceState.SPEAKING || voiceState === VoiceState.PREPARING_AUDIO
      ? kalkiText
      : transcript;

  // The CALL button now behaves CONTEXTUALLY — one tap does the right thing
  // for whatever state the conversation is currently in. This replaces the
  // old blunt "toggle start/end" that caused premature endings and overlap.
  // Note: the old `onCallClick` (tap-to-cycle CALL/SPEAK/DONE/INTERRUPT) and
  // the subsequent push-to-talk wrapper were both retired. All turn control
  // is now handled through onTapTalk (a single-tap state machine).

  // The AUDIO button toggles TTS output. Always clickable. First tap also unlocks audio.
  const onAudioToggle = async () => {
    try { console.info("[KALKI VOICE] AUDIO_TOGGLE_CLICKED current=", audioOn); } catch (_) {}
    if (!audioOn) {
      // Turning ON — must unlock audio inside the user gesture so future TTS plays.
      await voice.unlockAudioOnly();
    }
    setAudioOn((v) => !v);
  };

  // Direct ElevenLabs test — bypass STT, LLM, auth, Web Audio.
  // Uses /api/kalki/tts-test (fixed sentence, unauth) and appends a NATIVE
  // <audio controls> element so a human can play/pause/see duration.
  const onTestKrishnaVoice = async () => {
    if (testing) return;
    setTesting(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      console.info("[KALKI VOICE] DIRECT_TEST_CLICKED");
      const response = await fetch(`${BACKEND_URL}/api/kalki/tts-test`, {
        method: "POST",
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error(`TTS HTTP ${response.status}`);
      }
      const contentType = response.headers.get("content-type") || "";
      console.info("[KALKI VOICE] TTS content type:", contentType);
      const blob = await response.blob();
      console.info("[KALKI VOICE] TTS blob size:", blob.size);
      if (!contentType.startsWith("audio/") || blob.size === 0) {
        throw new Error("EMPTY_AUDIO or bad content-type");
      }
      const url = URL.createObjectURL(blob);
      // Remove any previous test element
      const prev = document.getElementById("kalki-test-audio");
      if (prev) prev.remove();
      const audio = document.createElement("audio");
      audio.id = "kalki-test-audio";
      audio.controls = true;
      audio.autoplay = false;
      audio.src = url;
      audio.volume = 1;
      audio.muted = false;
      audio.playsInline = true;
      audio.style.cssText = "position:fixed;left:50%;bottom:118px;transform:translateX(-50%);z-index:9999;width:min(90vw,520px);background:#fff;border-radius:14px;box-shadow:0 20px 60px rgba(0,0,0,.35);padding:6px;";
      // Phase 7 event logs
      audio.onloadstart     = () => console.info("[KALKI TEST] onloadstart");
      audio.onloadedmetadata= () => console.info("[KALKI TEST] onloadedmetadata dur=", audio.duration);
      audio.onloadeddata    = () => console.info("[KALKI TEST] onloadeddata readyState=", audio.readyState);
      audio.oncanplay       = () => console.info("[KALKI TEST] oncanplay");
      audio.onplay          = () => console.info("[KALKI TEST] onplay");
      audio.onplaying       = () => { console.info("[KALKI TEST] onplaying ✓ AUDIBLE"); setDiag((d) => ({ ...d, playing: true, curr: audio.currentTime, dur: audio.duration })); };
      audio.onpause         = () => console.info("[KALKI TEST] onpause");
      audio.onended         = () => console.info("[KALKI TEST] onended");
      audio.onerror         = () => console.warn("[KALKI TEST] onerror code=", audio.error?.code);
      audio.onstalled       = () => console.warn("[KALKI TEST] onstalled");
      audio.onwaiting       = () => console.info("[KALKI TEST] onwaiting");
      document.body.appendChild(audio);
      setDiag({ status: response.status, type: contentType, bytes: blob.size, playing: false, curr: 0, dur: 0, playPromise: "pending" });
      try {
        await audio.play();
        setDiag((d) => ({ ...d, playPromise: "resolved" }));
      } catch (e) {
        setDiag((d) => ({ ...d, playPromise: `rejected:${e?.name || "err"}` }));
        // Autoplay blocked — the <audio controls> element remains visible so
        // the user can press the native play button themselves.
      }
    } catch (e) {
      console.warn("[KALKI VOICE ERROR] DIRECT_TEST_FAIL", e?.message || e);
      setDiag((d) => ({ ...d, error: e?.message || String(e) }));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div
      className="relative w-full overflow-hidden text-white"
      style={{ height: "100dvh", background: GRAPHITE }}
      data-testid="talk-krishna"
    >
      <div style={{position: "absolute", top: 0, left: 0, right: 0, zIndex: 40}}>
        <KalkiGlobalHeader />
      </div>
      {/* subtle depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 55%, rgba(93,0,255,0.08) 0%, rgba(28,28,28,0) 60%)",
        }}
      />

      {/* Voice 6.0 — one-time Enable Microphone banner, shown only when the
          OS has not yet granted permission. Avoids auto-prompting (iOS blocks
          that) and keeps the main hero free of permission chrome once granted. */}
      {(micPerm === "prompt" || micPerm === "unknown") && (
        <div data-testid="talk-mic-permission"
             style={{position: "absolute", top: 92, left: 0, right: 0, zIndex: 20,
                      display: "flex", justifyContent: "center",
                      padding: "0 20px", pointerEvents: "auto"}}>
          <button onClick={requestMic}
                  data-testid="talk-enable-mic"
                  style={{background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                           color: "white", border: 0, padding: "11px 22px",
                           borderRadius: 999, fontSize: 11,
                           letterSpacing: "0.18em", cursor: "pointer"}}>
            ENABLE MICROPHONE
          </button>
        </div>
      )}
      {micPerm === "denied" && (
        <div data-testid="talk-mic-denied"
             style={{position: "absolute", top: 92, left: 0, right: 0, zIndex: 20,
                      display: "flex", justifyContent: "center",
                      padding: "0 24px", pointerEvents: "auto"}}>
          <div style={{background: "rgba(0,0,0,0.5)",
                        border: "1px solid rgba(255,46,154,0.3)",
                        borderRadius: 14, padding: "10px 16px",
                        color: "#FFC9EB", fontSize: 12, maxWidth: 480,
                        textAlign: "center", lineHeight: 1.5}}>
            Microphone is blocked. Enable it in your browser's site settings and reload to continue the call.
          </div>
        </div>
      )}

      {/* OLD inline header removed — the global `KalkiGlobalHeader` above
          already renders KALKI wordmark + nav + credits + profile dropdown,
          and the END control lives on the right of the global header's user
          dropdown as "Switch Account" / "Log out". The dedicated END pill
          moved into the main footer row for Voice context. */}
      <header className="hidden">
        <button data-testid="talk-end"
                onClick={() => { voice.endSession(); navigate("/app"); }}
                aria-label="End conversation">END</button>
      </header>

      {/* CENTER: DP + rings + state + live transcript, pinned to the
          exact centre of the available area between the fixed header
          (72px + top safe-area) and the bottom controls (160px). */}
      <main
        className="talk-stage"
        style={{
          position: "absolute",
          top: "calc(72px + env(safe-area-inset-top))",
          right: 0, bottom: 0, left: 0,
          display: "grid",
          gridTemplateRows: "1fr auto",
          placeItems: "center",
          paddingLeft: 24, paddingRight: 24,
          paddingBottom: "160px",
          overflow: "hidden",
          minWidth: 0, maxWidth: "100%",
        }}
      >
        <div className="talk-stage-center" style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", width: "100%", minWidth: 0,
          maxWidth: "min(640px, 100%)",
        }}>
        <div
          className="relative select-none"
          data-testid="talk-dp-wrap"
          data-listening={listening ? "1" : "0"}
          onClick={onTapTalk}
          style={{
            cursor: "pointer",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
            userSelect: "none",
            transition: "transform 160ms ease, filter 160ms ease",
            transform: listening ? "scale(1.03)" : "scale(1)",
            filter: listening ? "brightness(1.08)" : "brightness(1)",
          }}
        >
          <KalkiRings state={voiceState} amplitude={amplitude} />
          <KrishnaHero state={voiceState} amplitude={amplitude} />
          <UserBadge user={user} state={voiceState} />
        </div>

        {/* STATE MESSAGE */}
        <div
          data-testid="talk-state"
          className="mt-8 sm:mt-10 text-[12px] sm:text-[13px] tracking-[0.34em] font-light opacity-90"
          style={{
            color:
              voiceState === VoiceState.LISTENING ? "#F8FAFC"
              : voiceState === VoiceState.SPEAKING ? "#F8FAFC"
              : voiceState === VoiceState.ERROR ? "#FF9F7A"
              : "#E2E2E8",
            minHeight: 20,
          }}
        >
          <StateMessage state={voiceState} />
        </div>

        {/* LIVE TRANSCRIPT (user) or KALKI SENTENCE */}
        <div
          data-testid="talk-live-text"
          className="mt-5 sm:mt-6 text-center font-light"
          style={{
            maxWidth: "min(640px, 86vw)",
            fontSize: "clamp(15px, 2.1vmin, 18px)",
            lineHeight: 1.55,
            color: voiceState === VoiceState.SPEAKING ? "#F8FAFC" : "#C9C9D3",
            minHeight: 48,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {idle ? (
            <span className="opacity-70">Tap the <strong style={{ color: "white" }}>KALKI</strong> button to speak.</span>
          ) : liveText ? (
            <>{liveText}</>
          ) : null}
        </div>

        {/* REPLAY: tiny chip after Krishna finishes — reuses cached blob, no re-charge */}
        {!idle && voiceState === VoiceState.LISTENING && voice.canReplay && (
          <button
            data-testid="talk-replay"
            onClick={() => voice.replayLast()}
            className="mt-6 flex items-center gap-2 rounded-full px-4 py-2 text-[11px] tracking-[0.3em] opacity-70 hover:opacity-100 transition"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.14)",
              color: "white",
            }}
            title="Hear Krishna's last reply again (free)"
          >
            ↻ REPLAY
          </button>
        )}

        {/* ORCHESTRATOR SURFACES — Vedic Sky + contextual chip (dark-theme wrapper) */}
        {!idle && voiceState === VoiceState.LISTENING && (vedicSky || experience) && (
          <div className="mt-5 w-full max-w-[520px]" data-testid="talk-orch-surface">
            {vedicSky && (
              <div
                className="rounded-2xl px-4 py-3"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <div className="text-[10px] tracking-[0.32em] mb-2" style={{ color: LIME }}>
                  ✦ YOUR VEDIC SKY
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                  {vedicSky.planets.map((p, i) => (
                    <div key={i} className="flex items-baseline gap-1.5">
                      <span className="text-[11px] tracking-[0.14em] uppercase opacity-60">{p.planet}</span>
                      <span className="text-[13px] font-medium">{p.sign || "—"}</span>
                      {p.house && <span className="text-[10px] opacity-50">· h{p.house}</span>}
                    </div>
                  ))}
                  {vedicSky.current_dasha?.lord && (
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[11px] tracking-[0.14em] uppercase opacity-60">Period</span>
                      <span className="text-[13px] font-medium">{vedicSky.current_dasha.lord}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {experience && (
              <button
                data-testid={`talk-chip-${experience.kind}`}
                onClick={() => {
                  if (experience.kind === "ask_vedic") navigate("/app");
                  else navigate(`/app/chat?prompt=${encodeURIComponent(experience.label)}`);
                }}
                className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] tracking-[0.26em] uppercase hover:opacity-100 opacity-85 transition"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: `1px solid ${LIME}44`,
                  color: "white",
                }}
              >
                <Sparkles className="w-3.5 h-3.5" style={{ color: LIME }} />
                {experience.label} →
              </button>
            )}
          </div>
        )}

        {/* ERROR: HEAR KRISHNA retry (reuses cached blob — no re-charge) */}
        {voiceState === VoiceState.ERROR && (
          <div className="mt-8 flex flex-col items-center gap-3">
            <p className="text-[13px] opacity-75 text-center max-w-[32ch]">{errorMsg || "Something went wrong."}</p>
            <button
              data-testid="talk-hear-krishna"
              onClick={() => voice.retryPlayback()}
              className="px-6 py-3 rounded-full font-medium text-[12px] tracking-[0.3em]"
              style={{
                background: "white",
                color: GRAPHITE,
                boxShadow: `0 0 24px ${LIME}66`,
              }}
            >
              <Volume2 className="w-4 h-4 inline mr-2 -mt-1" />
              HEAR KRISHNA
            </button>
          </div>
        )}

        {!supportsSpeech && (
          <p className="mt-6 text-[12px] text-[#FF9F7A] opacity-80 text-center max-w-[36ch]">
            Voice input is not available in this browser. Please use Chrome or Edge.
          </p>
        )}
        </div>
      </main>

      {/* FOOTER controls — always visible, always tappable */}
      <footer
        className="absolute bottom-0 left-0 right-0 z-10 flex flex-col items-center gap-3 pb-6 sm:pb-8"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 24px)" }}
      >
        {/* Dev-only diagnostic panel */}
        {(diag.status !== null || diag.error) && (
          <div
            data-testid="voice-diagnostic-panel"
            className="mb-2 w-[min(92vw,520px)] text-[10px] tracking-[0.12em] rounded-xl px-3 py-2 font-mono"
            style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)", color: "#F8FAFC" }}
          >
            <div className="opacity-80 mb-1">KALKI VOICE DIAGNOSTIC</div>
            <div>status: <span style={{color: diag.status===200?LIME:FUCHSIA}}>{diag.status ?? "–"}</span></div>
            <div>type: {diag.type ?? "–"}</div>
            <div>bytes: {diag.bytes ?? "–"}</div>
            <div>play promise: <span style={{color: diag.playPromise==="resolved"?LIME:FUCHSIA}}>{diag.playPromise ?? "–"}</span></div>
            <div>onplaying: <span style={{color: diag.playing?LIME:FUCHSIA}}>{diag.playing ? "✓ AUDIBLE" : "✕ not yet"}</span></div>
            {diag.dur ? <div>time: {diag.curr?.toFixed?.(1) ?? 0}s / {diag.dur?.toFixed?.(1)}s</div> : null}
            {diag.error && <div style={{color: FUCHSIA}}>error: {diag.error}</div>}
          </div>
        )}

        <div className="flex items-center justify-center gap-5 sm:gap-7">
          {/* AUDIO toggle — always tappable */}
          <button
            data-testid="talk-audio-toggle"
            onClick={onAudioToggle}
            className="flex flex-col items-center gap-1.5 group"
            aria-label={audioOn ? "Turn Krishna's voice off" : "Turn Krishna's voice on"}
          >
            <span
              className="flex items-center justify-center rounded-full transition-all"
              style={{
                width: 54, height: 54,
                background: audioOn ? `${INDIGO}22` : "rgba(255,255,255,0.06)",
                border: audioOn ? `1px solid ${INDIGO}88` : "1px solid rgba(255,255,255,0.14)",
                boxShadow: audioOn ? `0 0 18px ${INDIGO}66` : "none",
              }}
            >
              {audioOn
                ? <Volume2 className="w-5 h-5" style={{ color: "#F8FAFC" }} />
                : <VolumeX className="w-5 h-5 opacity-70" />}
            </span>
            <span className="text-[9px] tracking-[0.32em] opacity-80">AUDIO {audioOn ? "ON" : "OFF"}</span>
          </button>

          {/* TAP-TO-TALK button — one tap starts recording, one tap ends.
              Replaces the earlier push-to-hold interaction per the Voice
              Talk 3.0 brief. */}
          <button
            data-testid="talk-call"
            data-intent={listening ? "recording" : (voiceState === VoiceState.SPEAKING ? "interrupt" : "tap")}
            data-listening={listening ? "1" : "0"}
            onClick={onTapTalk}
            className="relative flex flex-col items-center gap-1.5 select-none"
            aria-label={listening ? "Tap to finish" : "Tap to talk"}
            style={{ WebkitUserSelect: "none", WebkitTouchCallout: "none", userSelect: "none" }}
          >
            <span
              className="flex items-center justify-center rounded-full transition-all"
              style={{
                width: 76, height: 76,
                background: listening
                  ? `linear-gradient(135deg, ${FUCHSIA}, #C70062)`
                  : (voiceState === VoiceState.SPEAKING || voiceState === VoiceState.PREPARING_AUDIO)
                  ? `linear-gradient(135deg, ${INDIGO}, #8A52FF)`
                  : (voiceState === VoiceState.THINKING || voiceState === VoiceState.KRISHNA_THINKING)
                  ? "rgba(255,255,255,0.08)"
                  : `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
                boxShadow: listening
                  ? `0 10px 36px ${FUCHSIA}66, 0 0 24px ${FUCHSIA}88`
                  : ((voiceState === VoiceState.THINKING || voiceState === VoiceState.KRISHNA_THINKING) ? "none" : `0 10px 36px ${INDIGO}66, 0 0 24px ${FUCHSIA}44`),
                transition: "transform 160ms ease, box-shadow 160ms ease, background 160ms ease",
              }}
            >
              {listening
                ? <Mic className="w-7 h-7 text-white" />
                : (voiceState === VoiceState.THINKING || voiceState === VoiceState.KRISHNA_THINKING
                    ? <Sparkles className="w-7 h-7 text-white opacity-70" />
                    : <Phone className="w-7 h-7 text-white" />)}
            </span>
            <span className="text-[9px] tracking-[0.32em] font-medium">
              {listening
                ? "TAP TO FINISH"
                : voiceState === VoiceState.THINKING || voiceState === VoiceState.KRISHNA_THINKING
                ? "REFLECTING…"
                : voiceState === VoiceState.PREPARING_AUDIO
                ? "PREPARING…"
                : voiceState === VoiceState.SPEAKING
                ? "TAP TO INTERRUPT"
                : "TAP TO TALK"}
            </span>
          </button>

          {/* CHAT shortcut */}
          <button
            data-testid="talk-chat"
            onClick={() => { voice.endSession(); navigate("/app/chat"); }}
            className="flex flex-col items-center gap-1.5"
            aria-label="Switch to chat"
          >
            <span
              className="flex items-center justify-center rounded-full"
              style={{
                width: 54, height: 54,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.14)",
              }}
            >
              <MessageSquare className="w-5 h-5 opacity-85" />
            </span>
            <span className="text-[9px] tracking-[0.32em] opacity-80">CHAT</span>
          </button>
        </div>

        {/* Voice 6.0 — MODE toggle removed. Push-to-talk is the ONLY
            interaction model (press-and-hold to speak, release to send).
            Hands-free auto-restart has been retired to prevent overlapping
            audio and double-charges. */}

        {/* DEV / self-test row removed per user request */}
      </footer>

      {/* Local keyframes */}
      <style>{`
        @keyframes kalki-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes kalki-breathe { 0%,100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.03); opacity: 1; } }
        @keyframes krishna-breath { 0%,100% { filter: brightness(1) saturate(1); transform: scale(1); } 50% { filter: brightness(1.08) saturate(1.1); transform: scale(1.02); } }
        @keyframes user-pulse { 0%,100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.08); opacity: 1; } }
      `}</style>
    </div>
  );
}

/* ====== Hidden diagnostic page: /app/talk?debug=1 ====== */
function Row({ label, status }) {
  const color = status === "ok" ? "#CFFF04" : status === "fail" ? "#FF2E9A" : "#A1A1AA";
  const mark = status === "ok" ? "✓" : status === "fail" ? "✗" : "·";
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/10">
      <span className="text-[12px] tracking-[0.22em] opacity-80">{label}</span>
      <span className="text-[14px]" style={{ color }}>{mark}</span>
    </div>
  );
}

function VoiceDiagnostic({ voice, user }) {
  const [checks, setChecks] = useState({
    output: null, elevenlabs: null, mic: null, transcript: null, ai: null, bytes: null, playing: null, ended: null,
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const runOutput = async () => {
    try {
      const el = new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=");
      await el.play();
      setChecks((c) => ({ ...c, output: "ok" }));
    } catch (e) {
      setChecks((c) => ({ ...c, output: "fail" }));
    }
  };
  const runEleven = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/kalki/tts`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: "Hello. I am KALKI. I am here with you." }),
      });
      const type = r.headers.get("content-type") || "";
      const b = await r.blob();
      if (r.ok && type.startsWith("audio/") && b.size > 0) {
        setChecks((c) => ({ ...c, elevenlabs: "ok", bytes: "ok" }));
        const url = URL.createObjectURL(b);
        const el = new Audio(url);
        el.onplaying = () => setChecks((c) => ({ ...c, playing: "ok" }));
        el.onended = () => setChecks((c) => ({ ...c, ended: "ok" }));
        await el.play();
      } else {
        setChecks((c) => ({ ...c, elevenlabs: "fail" }));
      }
    } catch {
      setChecks((c) => ({ ...c, elevenlabs: "fail" }));
    }
  };
  const runMic = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      setChecks((c) => ({ ...c, mic: "ok" }));
    } catch {
      setChecks((c) => ({ ...c, mic: "fail" }));
    }
  };

  return (
    <div className="w-full min-h-[100dvh] text-white" style={{ background: GRAPHITE }}>
      <div className="max-w-[560px] mx-auto px-6 py-10">
        <h1 className="text-[14px] tracking-[0.4em] opacity-80">VOICE SYSTEM TEST</h1>
        <p className="text-[12px] opacity-50 mt-1">Hidden diagnostic — {user?.email}</p>

        <section className="mt-8">
          <h2 className="text-[12px] tracking-[0.3em] opacity-80 mb-3">STEP 1 — AUDIO OUTPUT</h2>
          <button onClick={runOutput} className="px-5 py-2 rounded-full border border-white/20 text-[12px] tracking-[0.2em]">
            PLAY TEST SOUND
          </button>
        </section>

        <section className="mt-8">
          <h2 className="text-[12px] tracking-[0.3em] opacity-80 mb-3">STEP 2 — ELEVENLABS KALKI VOICE</h2>
          <button onClick={runEleven} className="px-5 py-2 rounded-full border border-white/20 text-[12px] tracking-[0.2em]">
            TEST KALKI VOICE
          </button>
        </section>

        <section className="mt-8">
          <h2 className="text-[12px] tracking-[0.3em] opacity-80 mb-3">STEP 3 — MICROPHONE</h2>
          <button onClick={runMic} className="px-5 py-2 rounded-full border border-white/20 text-[12px] tracking-[0.2em]">
            TEST MICROPHONE
          </button>
        </section>

        <section className="mt-10 pt-6 border-t border-white/10">
          <Row label="AUDIO OUTPUT"     status={checks.output} />
          <Row label="MICROPHONE"       status={checks.mic} />
          <Row label="ELEVENLABS"       status={checks.elevenlabs} />
          <Row label="AUDIO RECEIVED"   status={checks.bytes} />
          <Row label="AUDIO PLAYING"    status={checks.playing} />
          <Row label="AUDIO ENDED"      status={checks.ended} />
        </section>
      </div>
    </div>
  );
}
